import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { prepareHeaders } from '../configuration/tokenHandling/prepareHeaders';
import moment from 'moment';
import {
    accountActivities,
    aggregatedAccountStatusList,
    aggregatedBookings,
    aggregatedDailyActivities,
    aggregatedStatusList,
    type AccountActivities,
    type AggregatedAccountStatusList,
    type AggregatedBookings,
    type AggregatedDailyActivities,
    type AggregatedStatusList,
    accountEvents,
    type AccountEvents,
    type Customer,
    customer,
    customers,
} from '../api/__generated_api';

type fetchStatusArgs = {
    start: string;
    include_rio: boolean;
};

type fetchAccountStatusArgs = {
    account_id: string;
    start: string;
};

type fetchActivityArgs = {
    start: string;
    end: string;
    include_rio: boolean;
};

type fetchAccountActivityArgs = {
    account_id: string;
    start: string;
    end: string;
};

type fetchAccountEventsArgs = {
    account_id: string;
    start: string;
};

type fetchCustomerAccount = {
    account_id: string;
};

export const monitorApi = createApi({
    reducerPath: 'monitorApi',
    tagTypes: ['Customer'],
    baseQuery: fetchBaseQuery({
        // baseUrl: 'http://localhost:8080/api/', // Set token in prepareHeaders.ts to run against local server
        baseUrl: 'https://pocket-driver-monitor.pocket-driver.rio.cloud/api/',
        prepareHeaders,
    }),
    endpoints: builder => ({
        fetchStatus: builder.query<AggregatedStatusList, fetchStatusArgs>({
            query: ({ start, include_rio }) => ({
                url: `status?include_rio=${include_rio}&start=${start}`,
                responseHandler: async (response: Response) => {
                    const data = await response.json();
                    return aggregatedStatusList.parse(data);
                },
            }),
            keepUnusedDataFor: 10,
        }),
        fetchAccountStatus: builder.query<AggregatedAccountStatusList, fetchAccountStatusArgs>({
            query: ({ account_id, start }) => ({
                url: `status/${account_id}?start=${start}`,
                responseHandler: async (response: Response) => {
                    const data = await response.json();
                    return aggregatedAccountStatusList.parse(data);
                },
            }),
            keepUnusedDataFor: 10,
        }),
        fetchActivity: builder.query<AggregatedDailyActivities, fetchActivityArgs>({
            query: ({ start, end, include_rio }) => ({
                url: `activity?include_rio=${include_rio}&start=${start}&end=${end}`,
                responseHandler: async (response: Response) => {
                    const data = await response.json();
                    const parsedData = aggregatedDailyActivities.parse(data);

                    const result = addMissingDates({
                        data: parsedData.activities,
                        start,
                        end,
                        defaultEntry: dateStr => ({
                            date: dateStr,
                            users: 0,
                            users_with_app_login: 0,
                            users_with_driver_login: 0,
                            users_with_order_com_rio_fms: 0,
                            users_with_order_com_third_party: 0,
                            users_with_cartright: 0,
                            users_with_cartright_standard: 0,
                            users_with_cartright_enterprise: 0,
                            users_with_unknown_booking: 0,
                            users_without_booking: 0,
                            activities: [],
                        }),
                    });

                    return { ...parsedData, activities: result };
                },
            }),
            keepUnusedDataFor: 10,
        }),
        fetchAccountActivity: builder.query<AccountActivities, fetchAccountActivityArgs>({
            query: ({ account_id, start, end }) => ({
                url: `activity/${account_id}?start=${start}&end=${end}`,
                responseHandler: async (response: Response) => {
                    const data = await response.json();
                    const parsedData = accountActivities.parse(data);

                    const result = addMissingDates({
                        data: parsedData.activities,
                        start,
                        end,
                        defaultEntry: dateStr => ({
                            date: dateStr,
                            users: 0,
                            activities: [],
                        }),
                    });

                    return { ...parsedData, activities: result };
                },
            }),
            keepUnusedDataFor: 10,
        }),
        fetchBookings: builder.query<AggregatedBookings, undefined>({
            query: () => ({
                url: 'bookings',
                responseHandler: async (response: Response) => {
                    const data = await response.json();
                    return aggregatedBookings.parse(data);
                },
            }),
            keepUnusedDataFor: 10,
        }),
        fetchCustomers: builder.query<Record<string, Customer>, undefined>({
            query: () => ({
                url: 'customers',
                responseHandler: async (response: Response) => {
                    const data = await response.json();
                    const customerList = customers.parse(data).customers;
                    return Object.fromEntries(customerList.map(customer => [customer.id, customer]));
                },
            }),
            keepUnusedDataFor: 60,
            providesTags: ['Customer'],
        }),
        fetchCustomer: builder.query<Customer, fetchCustomerAccount>({
            query: ({ account_id }) => ({
                url: `customers/${account_id}`,
                responseHandler: async (response: Response) => {
                    const data = await response.json();
                    return customer.parse(data);
                },
            }),
            keepUnusedDataFor: 60,
            providesTags: ['Customer'],
        }),
        updateCustomer: builder.mutation<void, Customer>({
            query: customer => ({
                url: 'customers',
                method: 'PUT',
                body: { customers: [customer] },
            }),
            invalidatesTags: ['Customer'],
        }),
        fetchAccountEvents: builder.query<AccountEvents, fetchAccountEventsArgs>({
            query: ({ account_id, start }) => ({
                url: `events/${account_id}?start=${start}`,
                responseHandler: async (response: Response) => {
                    const data = await response.json();
                    const parsedData = accountEvents.parse(data);

                    const result = addMissingDates({
                        data: parsedData.events,
                        start,
                        defaultEntry: dateStr => ({
                            date: dateStr,
                            tour_started: 0,
                            tour_finished: 0,
                            problem_reported: 0,
                        }),
                    });

                    return { ...parsedData, events: result };
                },
            }),
            keepUnusedDataFor: 10,
        }),
    }),
});

const addMissingDates = <T extends { date: string }>(params: {
    data: T[];
    start: string;
    end?: string;
    defaultEntry: (dateStr: string) => T;
}): T[] => {
    const { data, defaultEntry, start, end } = params;
    const dataMap = new Map(data.map(item => [item.date, item]));
    const result: T[] = [];
    const current = moment(start, 'YYYY-MM-DD').startOf('day');
    const endDate = end ? moment(end, 'YYYY-MM-DD') : moment();
    const maxDate = endDate.endOf('day');

    while (current.isSameOrBefore(maxDate)) {
        const dateStr = current.format('YYYY-MM-DD');
        result.push(dataMap.get(dateStr) ?? defaultEntry(dateStr));
        current.add(1, 'day');
    }

    return result;
};

// Export hooks for usage in function components which are
// auto-generated based on defined endpoints
export const {
    useFetchStatusQuery,
    useFetchAccountStatusQuery,
    useFetchActivityQuery,
    useFetchAccountActivityQuery,
    useFetchBookingsQuery,
    useFetchAccountEventsQuery,
    useFetchCustomersQuery,
    useFetchCustomerQuery,
    useUpdateCustomerMutation,
} = monitorApi;
